import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Typography, Button } from '@mui/material';
import { Grid, IconButton } from "@material-ui/core";
import { alertActions, authActions, mapCenterAction, masterActions } from '_store';
import { mapCenterValidationSchema } from "_utils/validationSchema";
import { supportedFormat } from '_utils/constant';
import { AutocompleteInput, UploadFiles, UnderConstruction, ModalPopup, PasswordInput } from '_components';
import { CompanyDetails, AdditionalDetails, CompanyPOC } from "container/user";
import { mapCenterRegistrationLabels } from '_utils/labels';
import { raphaelinfo, materialsymbolsdownload } from '../../images';
import { ERR_MSG } from 'constants/labels';
import { base64ToFile } from '_utils';

const ManageProfileMC = () => {
    const [showCompleteRegisterModal, setShowCompleteRegisterModal] = useState(false)
    const originalFormValuesRef = useRef(null);
    const dispatch = useDispatch();
    const logout = () => dispatch(authActions.logout());
    const navigate = useNavigate();
    const { portalkey, id } = useParams();
    const header = 'Map Center';
    const user = useSelector(x => x.mapcenter?.userData);
    const authUser = useSelector((x) => x.auth?.value);
    const isMCUserExpired = authUser?.Data?.UserDetails?.Status === "Expired";
    const isMCUserPartiallyApproved = authUser?.Data?.UserDetails?.Status === "Partially Approved";
    const isMCUserPartiallyVerified = authUser?.Data?.UserDetails?.Status === "Partially Verified";
    const [selectedDocumentType, setSelectedDocumentType] = useState(null);
    const [files, setFiles] = useState([]);
    const documentTypeData = user?.Data?.DocumentData || [];    
    const states = user?.Data?.State || [];
    const exsistingFiles = user?.Data?.FileData || [];
    const isUser = true;
    const [isEnable, setIsEnable] = useState(false);
    const documentData = documentTypeData.map(x => ({
        label: x.DocumentDescription,
        value: x.DocumentTypeID
    }));

    const filteredDocumentData = isMCUserPartiallyApproved
        ? documentData.filter(x => x.value === 3)
        : documentData;

    const stateData = states.map(x => ({
        label: x.StateName,
        value: x.StateId
    }));   

    const { register, handleSubmit, control, reset, formState: { errors, isValid }, trigger, getValues } = useForm({
        resolver: yupResolver(mapCenterValidationSchema(isMCUserExpired))
    });    
    useEffect(() => {
        const fetchData = async () => {
            try {
                dispatch(alertActions.clear());
                dispatch(mapCenterAction.clear()); 
                if(isMCUserExpired) {
                    const user = await dispatch(mapCenterAction.getExpiredUserDetails()).unwrap();
                    const data = user?.Data;
                    sessionStorage.setItem('mapcenterUserID', id);
                    reset(data);
                    originalFormValuesRef.current = data;                      
                    const allFields = Object.keys(getValues());
                    const fieldsToValidate = allFields.filter(
                    (field) => field !== "NewPassword" && field !== "ConfirmNewPassword"
                    );
                    await trigger(fieldsToValidate);
                } else if (isMCUserPartiallyApproved) {
                    const user = await dispatch(mapCenterAction.getPartiallyApprovedDetails()).unwrap();                    
                    const data = user?.Data;
                    sessionStorage.setItem('mapcenterUserID', id);
                    reset(data);
                    originalFormValuesRef.current = data;  
                    if (data?.FileData) {
                        setFiles(data?.FileData.map(file => ({
                            ID: file.ID,
                            AdditionalID: file.AdditionalID,
                            DocumentTypeID: file.DocumentTypeID,
                            FileName: file.FileName,
                            Format: file.Format,
                            Size: file.Size,
                            PortalKey: file.PortalKey,
                            File: file.File,
                            Url: file.Url
                        })));
                    }                    
                    const allFields = Object.keys(getValues());
                    const fieldsToValidate = allFields.filter(
                    (field) => field !== "NewPassword" && field !== "ConfirmNewPassword"
                    );
                    await trigger(fieldsToValidate);
                } else if (isMCUserPartiallyVerified) {
                    const user = await dispatch(mapCenterAction.getPartiallyVerifiedDetails()).unwrap();                    
                    const data = user?.Data;
                    sessionStorage.setItem('mapcenterUserID', id);
                    reset(data);
                    originalFormValuesRef.current = data;  
                    if (data?.FileData) {
                        setFiles(data?.FileData.map(file => ({
                            ID: file.ID,
                            AdditionalID: file.AdditionalID,
                            DocumentTypeID: file.DocumentTypeID,
                            FileName: file.FileName,
                            Format: file.Format,
                            Size: file.Size,
                            PortalKey: file.PortalKey,
                            File: file.File,
                            Url: file.Url
                        })));
                    }                    
                    const allFields = Object.keys(getValues());
                    const fieldsToValidate = allFields.filter(
                    (field) => field !== "NewPassword" && field !== "ConfirmNewPassword"
                    );
                    await trigger(fieldsToValidate);
                } else {
                  const user = await dispatch(mapCenterAction.getRegistrationDetails(id)).unwrap();
                  const data = user?.Data;
                  sessionStorage.setItem('mapcenterUserID', id);
                  reset(data);
                  originalFormValuesRef.current = data;
                }          
                              
                !isMCUserPartiallyApproved && !isMCUserPartiallyVerified && setFiles([])
            } catch (error) {
                dispatch(alertActions.error({
                    message: error.message ? ERR_MSG["073"] : error,
                    header: header
                }));
                reset(user);
            }
        };
        fetchData();    
    }, [id, dispatch, reset, portalkey, isMCUserExpired, isMCUserPartiallyApproved, isMCUserPartiallyVerified]);

    useEffect(() => {
        const missingDocumentTypes = documentTypeData.filter(docType =>
            !files.some(file => file.DocumentTypeID === docType.DocumentTypeID)
        );
        if (missingDocumentTypes.length > 0) {
            setIsEnable(false);
        } else {
            setIsEnable(true);

        }
    }, [files])

    const onSubmit = async (data) => {
        dispatch(alertActions.clear());
        try {
            // Validate that all required document types have files
            const missingDocumentTypes = documentTypeData.filter(docType =>
                !files.some(file => file.DocumentTypeID === docType.DocumentTypeID)
            );

            if (!documentData || missingDocumentTypes.length > 0) {
                const missingDescriptions = missingDocumentTypes.map(docType => docType.DocumentDescription).join(', ');
                dispatch(alertActions.error({
                    message: `Missing files for document types: ${missingDescriptions}`,
                    header: header
                }));
                return;
            }

            const formData = new FormData();
            const formDataParApprove = new FormData()

            if (!isMCUserPartiallyApproved) {
                formData.append("UserID", user?.Data?.UserID);
                formData.append("FullName", data.FullName);
                formData.append("AlternateEmail", data.AlternateEmail);
                formData.append("DLState", data.DLState);
                formData.append("DLNumber", data.DLNumber);
                formData.append("HomeStreetAddress1", data.HomeStreetAddress1);
                formData.append("HomeStreetAddress2", data.HomeStreetAddress2 || '');
                formData.append("HomeCity",  data.HomeCity);
                formData.append("HomeState",  data.HomeState);
                formData.append("HomeZipCode",  data.HomeZipCode);
                formData.append("CompanyName",  data.CompanyName);
                formData.append("TaxIdentificationNumber",  data.TaxIdentificationNumber);
                formData.append("CompanyStreetAddress1",  data.CompanyStreetAddress1);
                formData.append("CompanyStreetAddress2",  data.CompanyStreetAddress2 || '');
                formData.append("CompanyCity",  data.CompanyCity);
                formData.append("CompanyState",  data.CompanyState);
                formData.append("CompanyZipCode",  data.CompanyZipCode);
                formData.append("CompanyContactName",  data.CompanyContactName);
                formData.append("CompanyContactTelephone",  data.CompanyContactTelephone);
                formData.append("CompanyContactEmailAddress",  data.CompanyContactEmailAddress);
                formData.append("AuthorizedWGLContact",  data.AuthorizedWGLContact);
                formData.append("AdditionalID",  user?.Data?.AdditionalID || 0);
                formData.append("StatusID",  1);
                formData.append("MobileNumber",  data.MobileNumber);
                formData.append("AlternatePhoneNumber",  data.AlternatePhoneNumber);
                formData.append("EmailID",  user?.Data?.EmailID);
                formData.append("ModifiedBy",  data.FullName);
                id && formData.append("Token",  id);
                if (files && files.length > 0) {                    
                    Array.from(files).forEach((file) => {
                        if(file?.File) {
                            formData.append("FileData", file.File);
                            formData.append("DocumentTypeIDs", file.DocumentTypeID); 
                        }                                        
                    });
                }
                
            } else {
                const file = files?.find((file => file?.DocumentTypeID === 3));
                formDataParApprove.append("Data.File", file.File);
                formDataParApprove.append("Data.DocumentTypeID", file.DocumentTypeID);                
            }
                                   
            if (isMCUserExpired) {                                     
                const formDataExpired = new FormData()
                formDataExpired.append("Data.NewPassword",  data.NewPassword);
                formDataExpired.append("Data.UserID", user?.Data?.UserID);
                formDataExpired.append("Data.FullName", data.FullName);
                formDataExpired.append("Data.AlternateEmail", data.AlternateEmail);
                formDataExpired.append("Data.DLState", data.DLState);
                formDataExpired.append("Data.DLNumber", data.DLNumber);
                formDataExpired.append("Data.HomeStreetAddress1", data.HomeStreetAddress1);
                formDataExpired.append("Data.HomeStreetAddress2", data.HomeStreetAddress2 || '');
                formDataExpired.append("Data.HomeCity",  data.HomeCity);
                formDataExpired.append("Data.HomeState",  data.HomeState);
                formDataExpired.append("Data.HomeZipCode",  data.HomeZipCode);
                formDataExpired.append("Data.CompanyName",  data.CompanyName);
                formDataExpired.append("Data.TaxIdentificationNumber",  data.TaxIdentificationNumber);
                formDataExpired.append("Data.CompanyStreetAddress1",  data.CompanyStreetAddress1);
                formDataExpired.append("Data.CompanyStreetAddress2",  data.CompanyStreetAddress2 || '');
                formDataExpired.append("Data.CompanyCity",  data.CompanyCity);
                formDataExpired.append("Data.CompanyState",  data.CompanyState);
                formDataExpired.append("Data.CompanyZipCode",  data.CompanyZipCode);
                formDataExpired.append("Data.CompanyContactName",  data.CompanyContactName);
                formDataExpired.append("Data.CompanyContactTelephone",  data.CompanyContactTelephone);
                formDataExpired.append("Data.CompanyContactEmailAddress",  data.CompanyContactEmailAddress);
                formDataExpired.append("Data.AuthorizedWGLContact",  data.AuthorizedWGLContact);
                formDataExpired.append("Data.AdditionalID",  user?.Data?.AdditionalID || 0);
                formDataExpired.append("Data.StatusID",  1);
                formDataExpired.append("Data.MobileNumber",  data.MobileNumber);
                formDataExpired.append("Data.AlternatePhoneNumber",  data.AlternatePhoneNumber);
                formDataExpired.append("Data.EmailID",  user?.Data?.EmailID);
                formDataExpired.append("Data.ModifiedBy",  data.FullName);
                id && formDataExpired.append("Data.Token",  id);
                if (files && files.length > 0) {
                    Array.from(files).forEach((file) => {
                    if (file?.File) {
                        formDataExpired.append("Data.FileData", file.File);
                        formDataExpired.append("Data.DocumentTypeIDs", file.DocumentTypeID); 
                    }                                       
                    });
                }         
                await dispatch(
                mapCenterAction.updateExpired(formDataExpired),
                ).unwrap();
            } else if (isMCUserPartiallyApproved) {                                
                const res = await dispatch(
                    mapCenterAction.updatePartiallyApproved(formDataParApprove),
                ).unwrap();

                if (!res?.Succeeded) {
                    dispatch(
                        alertActions.error({
                            message: res?.Message || ERR_MSG["073"],
                            header: header,
                        }),
                    );
                    return
                }
                

            } else if (isMCUserPartiallyVerified) {
                const formDataPartVerified = new FormData()
                formDataPartVerified.append("Data.UserID", user?.Data?.UserID);
                formDataPartVerified.append("Data.FullName", data.FullName);
                formDataPartVerified.append("Data.AlternateEmail", data.AlternateEmail);
                formDataPartVerified.append("Data.DLState", data.DLState);
                formDataPartVerified.append("Data.DLNumber", data.DLNumber);
                formDataPartVerified.append("Data.HomeStreetAddress1", data.HomeStreetAddress1);
                formDataPartVerified.append("Data.HomeStreetAddress2", data.HomeStreetAddress2 || '');
                formDataPartVerified.append("Data.HomeCity",  data.HomeCity);
                formDataPartVerified.append("Data.HomeState",  data.HomeState);
                formDataPartVerified.append("Data.HomeZipCode",  data.HomeZipCode);
                formDataPartVerified.append("Data.CompanyName",  data.CompanyName);
                formDataPartVerified.append("Data.TaxIdentificationNumber",  data.TaxIdentificationNumber);
                formDataPartVerified.append("Data.CompanyStreetAddress1",  data.CompanyStreetAddress1);
                formDataPartVerified.append("Data.CompanyStreetAddress2",  data.CompanyStreetAddress2 || '');
                formDataPartVerified.append("Data.CompanyCity",  data.CompanyCity);
                formDataPartVerified.append("Data.CompanyState",  data.CompanyState);
                formDataPartVerified.append("Data.CompanyZipCode",  data.CompanyZipCode);
                formDataPartVerified.append("Data.CompanyContactName",  data.CompanyContactName);
                formDataPartVerified.append("Data.CompanyContactTelephone",  data.CompanyContactTelephone);
                formDataPartVerified.append("Data.CompanyContactEmailAddress",  data.CompanyContactEmailAddress);
                formDataPartVerified.append("Data.AuthorizedWGLContact",  data.AuthorizedWGLContact);
                formDataPartVerified.append("Data.AdditionalID",  user?.Data?.AdditionalID || 0);
                formDataPartVerified.append("Data.StatusID",  1);
                formDataPartVerified.append("Data.MobileNumber",  data.MobileNumber);
                formDataPartVerified.append("Data.AlternatePhoneNumber",  data.AlternatePhoneNumber);
                formDataPartVerified.append("Data.EmailID",  user?.Data?.EmailID);
                formDataPartVerified.append("Data.ModifiedBy",  data.FullName);
                id && formDataPartVerified.append("Data.Token",  id);
                if (files && files.length > 0) {
                  Array.from(files).forEach((file) => {
                    if (file?.File) {
                        formDataPartVerified.append("Data.FileData", file.File);
                        formDataPartVerified.append("Data.DocumentTypeIDs", file.DocumentTypeID); 
                    }                                       
                  });
                }
              await dispatch(
                mapCenterAction.updatePartiallyVerified(formDataPartVerified),
              ).unwrap();
            } else {
              await dispatch(
                mapCenterAction.update(formData),
              ).unwrap();
            }           

            sessionStorage.removeItem('mapcenterUserID');
            if(isMCUserExpired || isMCUserPartiallyApproved || isMCUserPartiallyVerified) {              
              setShowCompleteRegisterModal(true)
            } else {
              navigate('/');
              dispatch(alertActions.success({ message: mapCenterRegistrationLabels.message1, header: mapCenterRegistrationLabels.header, showAfterRedirect: true }));
            }
        } catch (error) {            
            dispatch(
              alertActions.error({
                message: error.message ? ERR_MSG["073"] : error,
                header: header,
              }),
            );
        }
    };    
    const handleBlur = async (e) => {
        const fieldName = e.target.name;
        await trigger(fieldName); // Trigger validation for the field
    };

    const handleOnChange = (event, newvalue) => {
        setSelectedDocumentType(newvalue);
    };

    const handleFileChange = (newFiles) => {
        setFiles(newFiles);
    };

    const handleDownload = async () => {        
        try {
            dispatch(alertActions.clear());
            const res = await dispatch(masterActions.getNondisclosureDocument()).unwrap();
            base64ToFile(res?.File, res?.FileName);
        } catch (error) {
            dispatch(alertActions.error({
                message: error.message ? ERR_MSG["073"] : error,
                header: header
            }));
        }
    };

    const handleCancelClick = () => {
        const currentValues = getValues();
        const originalValues = originalFormValuesRef.current;

        if (isMCUserExpired || isMCUserPartiallyApproved || isMCUserPartiallyVerified) {
            logout()
            return
        }
        if (JSON.stringify(currentValues) === JSON.stringify(originalValues)) {
            navigate('/');
        } else {
            setFiles(originalValues?.FileData || []);
            window.location.reload();
        }
    };

    return (
        <>
            {!(user?.loading || user?.error) && (
                <Typography component="div" className="MapCenterAccecss">
                    {(isMCUserPartiallyApproved || isMCUserPartiallyVerified) && (
                        <Typography 
                            component="div" 
                            sx={{
                                border: '1px solid red',
                                padding: '8px',
                                marginBottom: '16px',
                                marginTop: '16px',
                                borderRadius: '4px',
                                backgroundColor: '#fff5f5'
                            }}
                        >
                            <Typography                                  
                                sx={{ 
                                    color: 'red', 
                                    fontWeight: '500',
                                    margin: 0 
                                }}
                            >
                                <Typography component={"span"} fontWeight={600}>Partially {isMCUserPartiallyApproved ? "Approved " : "Verified "} Reason:</Typography>
                                {isMCUserPartiallyApproved ? ` ${user?.Data?.PartiallyApprovedComment}` : ` ${user?.Data?.PartiallyVerifiedComment}`}
                            </Typography>
                        </Typography>
                    )}
                    <Typography component="div" className="MapCenterAccecssheading">
                        <Typography component="h1" variant="h5">Map Center Access</Typography>
                    </Typography>
                    <form onSubmit={handleSubmit(onSubmit)}>
                        <Typography className="Personal-Information-container" component="div">
                            <Grid container spacing={3}>
                                <Grid item xs={12} sm={12} md={12}>
                                    <Grid container spacing={3}>
                                        <Grid item xs={12} sm={12} md={8}>
                                            <Grid container spacing={3}>                                                
                                                <Grid item xs={12} sm={6} md={6} className="Personal-Information">                                                                                                      
                                                    <Typography component="div" className="mapcontainer">
                                                        
                                                        <Typography component="div" className="Personal-Informationsheading">
                                                            <Typography component="h2" variant="h5">Personal Information</Typography>
                                                        </Typography>
                                                        <AdditionalDetails
                                                            handleBlur={handleBlur}
                                                            register={register}
                                                            control={control}
                                                            stateData={stateData}
                                                            errors={errors}
                                                            isUser={isUser}
                                                            isDisabled={isMCUserPartiallyApproved}
                                                        />
                                                    </Typography>
                                                </Grid>
                                                <Grid item xs={12} sm={6} md={6} className="Personal-Information">
                                                    <Typography component="div" className="mapcontainer">
                                                         {
                                                            isMCUserExpired && (
                                                                <Typography component="div" className="mapcontainer" mb={"24px"}>
                                                                    <Typography component="div" className="Personal-Informationsheading">
                                                                        <Typography component="h2" variant="h5">New Password</Typography>
                                                                    </Typography>                                                                
                                                                    <PasswordInput
                                                                        control={control}
                                                                        name="NewPassword"
                                                                        label="New Password"
                                                                        rules={{ required: 'New Password is required' }}
                                                                        errors={errors}
                                                                        handleBlur={handleBlur}
                                                                        isPasswordValid={true}
                                                                    />
                                                                    <PasswordInput
                                                                        control={control}
                                                                        name="ConfirmNewPassword"
                                                                        label="Confirm New Password"
                                                                        rules={{ required: 'Confirm New Password is required' }}
                                                                        errors={errors}
                                                                        handleBlur={handleBlur}
                                                                        isPasswordValid={true}
                                                                    />                                                                
                                                                </Typography>
                                                            )
                                                        } 
                                                        <Typography component="div" className="Personal-Informationsheading">
                                                            <Typography component="h2" variant="h5">Company Information</Typography>
                                                        </Typography>
                                                        <CompanyDetails
                                                            handleBlur={handleBlur}
                                                            register={register}
                                                            errors={errors}
                                                            control={control}
                                                            stateData={stateData}
                                                            isDisabled={isMCUserPartiallyApproved}
                                                        />
                                                    </Typography>
                                                </Grid>
                                                <Grid item xs={12} sm={12} md={12}>
                                                    <CompanyPOC
                                                        register={register}
                                                        errors={errors}
                                                        control={control}
                                                        handleBlur={handleBlur}
                                                        isDisabled={isMCUserPartiallyApproved}
                                                    />
                                                </Grid>
                                            </Grid>

                                        </Grid>
                                        <Grid item xs={12} sm={12} md={4}>
                                            <Typography component="div" className="UploadFiles-container mapcontainer  ">
                                                <Typography component="div" className="Personal-Informationsheading ">
                                                    <Typography component="h2" variant="h5">Document Upload  <img src={raphaelinfo} alt='raphaelinfo'></img></Typography>
                                                </Typography>
                                                <Typography component="div" className="passwordcheck marbottom0 selecticon">
                                                    <AutocompleteInput
                                                        control={control}
                                                        name="documentType"
                                                        label="Document Type"
                                                        options={filteredDocumentData}
                                                        error={!!errors.documentType}
                                                        helperText={errors.documentType?.message}
                                                        handleBlur={handleBlur}
                                                        onChange={handleOnChange}
                                                    />
                                                </Typography>
                                                <UploadFiles
                                                    initialFiles={files}
                                                    portalKey={portalkey}
                                                    selectedDocumentType={selectedDocumentType}
                                                    supportedFormats={supportedFormat}
                                                    documentTypes={documentTypeData}
                                                    control={control}
                                                    errors={errors}
                                                    onFileChange={handleFileChange}
                                                    exsistingFiles={exsistingFiles}
                                                    isPartiallyApproved={isMCUserPartiallyApproved}
                                                />
                                                <Typography component="div" className="SupportedFormats">
                                                    <Typography component="h3" >Download Template</Typography>
                                                    <div className="mar-top-16" >
                                                        <Typography component="div">Non-disclosure agreement
                                                            <IconButton onClick={handleDownload}>
                                                                <img src={materialsymbolsdownload} alt="material-symbols_download"></img>
                                                            </IconButton>
                                                        </Typography>
                                                    </div>
                                                </Typography>
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </Grid>

                            </Grid>
                        </Typography>
                        <Grid item xs={12} sm={12} md={12} className="Personal-Information">
                            <Button variant="contained" color="red" className="cancelbutton" onClick={handleCancelClick}>
                                Cancel
                            </Button>
                            <Button type="submit" variant="contained" className="CompleteRegistration" color="primary" disabled={!(isValid && isEnable)} >
                                Complete Registration
                            </Button>
                        </Grid>
                    </form>
                </Typography>
            )}
            {user?.error && <UnderConstruction />}
            {
              showCompleteRegisterModal && 
              (<ModalPopup
                header="Profile Registration Complete"
                message1="Your registration request is successfully submitted. You will be notified once your request is approved."
                btnPrimaryText="Close"
                handlePrimaryClick={logout}                
              />)
            }
        </>
    );
};

export default ManageProfileMC;